import { css } from 'aphrodite';
import * as React from 'react';
import Linkify from 'react-linkify';
import { DeprecatedCloseButton } from '../../../web/components/DeprecatedCloseButton';
import { RadioButton } from '../../../web/components/RadioButton';
import { baseStyleSheet } from '../../../web/styles/styles';
import { IFileAttachment, SocialMediaType } from '../../models/Api';
import { styleSheet } from './styles';

export const MainImageWidth = 372;

export const HalfImageWidth = MainImageWidth / 2 - 1;
export const ThirdImageWidth = MainImageWidth / 3 - 2;
export const MainImageHeight = 250;
export const HalfImageHeight = 150;

export const onRenderLink = (decoratedHref: string, decoratedText: string, key: number) => {
	return (
		<a
			key={key}
			className={css(baseStyleSheet.brandLink)}
			href={decoratedHref}
			target='_blank'
			rel='noreferrer'
		>{`${decoratedText}`}</a>
	);
};

interface IProps {
	previewContent?: string;
	postContent?: string;
	target?: SocialMediaType;
	truncate?: boolean;
}

export const PreviewPostContentView = ({ postContent, previewContent, target, truncate }: IProps) => {
	const [showAll, setShowAll] = React.useState(false);
	const onShowAll = () => {
		setShowAll(!showAll);
	};

	return (
		<div className={css(styleSheet.textOnlyPadding)}>
			{target === SocialMediaType.Facebook || target === SocialMediaType.LinkedIn ? (
				<Linkify componentDecorator={onRenderLink}>{showAll ? postContent : previewContent}</Linkify>
			) : (
				<>{showAll ? postContent : previewContent}</>
			)}
			{previewContent?.length > 0 &&
				truncate &&
				(showAll ? (
					<DeprecatedCloseButton className={css(styleSheet.closeButton)} fillColor='black' onClick={onShowAll} />
				) : (
					<button onClick={onShowAll}>
						<span className={css(baseStyleSheet.fontBold)}>... Read more</span>
					</button>
				))}
		</div>
	);
};

interface IPlatformPreviewImageViewProps {
	postImages?: IFileAttachment[];
	onClickEnlargeImage?: (imageUrl: string) => void;
}

export const InstagramImagePreview: React.FC<IPlatformPreviewImageViewProps> = props => {
	const { postImages, onClickEnlargeImage } = props;
	const [selectedImage, setSelectedImage] = React.useState(0);

	const onSelectedTabChanged = (indx: number) => () => {
		setSelectedImage(indx);
	};

	const count = postImages?.length;
	const isMultiple = count > 1;
	const imgHeight = !isMultiple ? 'auto' : MainImageHeight;

	return (
		<>
			<div className={css(styleSheet.imageDisplayAreaPreview, styleSheet.imageDisplayAreaPreviewInstagram)}>
				{postImages.map((image, i) => {
					return (
						<img
							onClick={() => onClickEnlargeImage(image.url)}
							key={`${postImages[selectedImage].url}-${i}`}
							className={css(styleSheet.tab, selectedImage === i && styleSheet.tabSelected)}
							src={image.url}
							width={MainImageWidth}
							height={imgHeight}
						/>
					);
				})}
			</div>
			<div className={`${css(styleSheet.tabNavigation)}`}>
				{postImages.map((_, i) => {
					return (
						<RadioButton
							checked={i === selectedImage || false}
							className={css(styleSheet.circle)}
							id={`${postImages[selectedImage].url}-${i}`}
							key={`${postImages[selectedImage].url}-${i}`}
							onChange={onSelectedTabChanged(i)}
						/>
					);
				})}
			</div>
		</>
	);
};

export const BasicImagePreview: React.FC<IPlatformPreviewImageViewProps> = props => {
	const { postImages, onClickEnlargeImage } = props;
	const count = postImages?.length;
	const isMultiple = count > 1;
	const imgHeight = !isMultiple ? 'auto' : MainImageHeight;
	return (
		<div className={css(styleSheet.imageDisplayAreaPreview, count > 4 && styleSheet.imageDisplayAreaPreviewColWrap)}>
			{postImages.map((image, i) => {
				if (((count === 2 || count === 3 || count === 4) && i === 0) || !isMultiple) {
					return (
						<button onClick={() => onClickEnlargeImage(image.url)} key={`${image.url}-${i}`}>
							<img src={image.url} width={MainImageWidth} height={imgHeight} />
						</button>
					);
				}

				if (count === 3) {
					return (
						<button
							onClick={() => onClickEnlargeImage(image.url)}
							key={`${image.url}-${i}`}
							className={css(i === 2 && styleSheet.imageDisplayLeft)}
						>
							<img src={image.url} width={HalfImageWidth} height={HalfImageHeight} />
						</button>
					);
				}

				if (count === 2) {
					return (
						<button onClick={() => onClickEnlargeImage(image.url)} key={`${image.url}-${i}`}>
							<img src={image.url} width={MainImageWidth} height={MainImageHeight} />
						</button>
					);
				}

				if (count === 4) {
					return (
						<button
							onClick={() => onClickEnlargeImage(image.url)}
							className={css((i === 3 || i === 2) && styleSheet.imageDisplayLeft)}
							key={`${image.url}-${i}`}
						>
							<img
								src={image.url}
								width={i === 3 ? ThirdImageWidth : ThirdImageWidth + 1}
								height={HalfImageHeight} // These are reverse because of col wrap
							/>
						</button>
					);
				}

				return (
					<button
						onClick={() => onClickEnlargeImage(image.url)}
						className={css((i === 2 || i === 3 || i === 4) && styleSheet.imageDisplayLeft)}
						key={`${image.url}-${i}`}
					>
						<img
							src={image.url}
							width={HalfImageWidth}
							height={i === 0 || i === 1 ? HalfImageWidth - 1 : ThirdImageWidth} // These are reverse because of col wrap
						/>
					</button>
				);
			})}
		</div>
	);
};

export const LinkedInImagePreview: React.FC<IPlatformPreviewImageViewProps> = props => {
	const { postImages, onClickEnlargeImage } = props;
	const count = postImages?.length;
	const isMultiple = postImages?.length > 1;
	const isOdd = postImages?.length % 2;
	const imgHeight = !isMultiple ? 'auto' : MainImageHeight;

	return (
		<div className={css(styleSheet.imageDisplayAreaPreview)}>
			{postImages.map((image, i) => {
				if (((count === 3 || count === 4) && i === 0) || !isMultiple) {
					return (
						<button key={`${image.url}-${i}`} onClick={() => onClickEnlargeImage(image.url)}>
							<img src={image.url} width={MainImageWidth} height={imgHeight} />
						</button>
					);
				}

				if (count === 3 || count === 2) {
					return (
						<button
							className={css(isOdd && i === 2 && styleSheet.imageDisplayLeft)}
							onClick={() => onClickEnlargeImage(image.url)}
							key={`${image.url}-${i}`}
						>
							<img src={image.url} width={HalfImageWidth} height={count === 2 ? 476 : HalfImageHeight} />
						</button>
					);
				}

				if (count === 4) {
					return (
						<button
							onClick={() => onClickEnlargeImage(image.url)}
							className={css((i === 2 || i === 3) && styleSheet.imageDisplayLeft)}
							key={`${image.url}-${i}`}
						>
							<img src={image.url} width={i === 3 ? ThirdImageWidth : ThirdImageWidth + 1} height={HalfImageHeight} />
						</button>
					);
				}

				return (
					<button
						className={css((i === 1 || i === 3 || i === 4) && styleSheet.imageDisplayLeft)}
						key={`${image.url}-${i}`}
						onClick={() => onClickEnlargeImage(image.url)}
					>
						<img
							src={image.url}
							width={i === 0 || i === 1 ? HalfImageWidth : i === 4 ? ThirdImageWidth : ThirdImageWidth + 1}
							height={i === 0 || i === 1 ? 240 : HalfImageHeight}
						/>
					</button>
				);
			})}
		</div>
	);
};
